import React from 'react';
import { Link } from 'react-router-dom';
import { T } from '../constants';
import { fmtDate, fmtTime, fmtPrice, mlColor, dirStyle, catColor, computePnL, detailHref } from '../helpers';
import GradeBadge from './badges/GradeBadge';
import SystemBadge from './badges/SystemBadge';
import ClaudeVerdictBadge from './badges/ClaudeVerdictBadge';


export default function FeaturedDecision({ d }) {
  if (!d) return (
    <div className="featured">
      <div className="empty-state" style={{ padding: '60px 20px' }}>
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="14" stroke="var(--tv-text-muted)" strokeWidth="1.5" strokeDasharray="4 3"/>
          <path d="M10 20 L14 15 L18 17 L22 11" stroke="var(--tv-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span style={{ fontSize: 12 }}>Select a decision to inspect</span>
      </div>
    </div>
  );

  // TIER strategies put the verdict in `action_label`, not `action` (which is
  // the row kind: "entry"/"exit"). Prefer `action_label` when present so a
  // BLOCKed tier row renders its verdict instead of the raw kind.
  const actionEff   = d.action_label || d.action;
  const isTrade     = actionEff === 'TRADE';
  const accent      = isTrade ? T.bull : T.bear;
  const accentBg    = isTrade ? T.bullBg : T.bearBg;
  const accentBdr   = isTrade ? T.bullBorder : T.bearBorder;
  const href        = detailHref(d);

  return (
    <div className="featured" style={{ borderColor: accentBdr }}>
      {/* Header */}
      <div className="featured-header" style={{ background: accentBg, borderBottomColor: accentBdr }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', align: 'center', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <SystemBadge systemId={d.system_id} />
            {d.direction && (
              <span className="badge" style={{ ...dirStyle(d.direction), fontSize: 10 }}>{d.direction}</span>
            )}
            {d.category && (
              <span className="badge" style={{
                background: `${catColor(d.category)}18`,
                border: `1px solid ${catColor(d.category)}40`,
                color: catColor(d.category), fontSize: 10,
              }}>{d.category}</span>
            )}
            {d.phase1_applied && (
              <span className="badge" style={{
                background: 'rgba(240,185,11,0.1)', border: '1px solid rgba(240,185,11,0.3)',
                color: T.gold, fontSize: 10,
              }}>Phase 1: {d.phase1_applied}</span>
            )}
          </div>
          <span style={{ fontSize: 11, color: T.textMuted, fontFamily: 'Roboto Mono, monospace' }}>
            {fmtDate(d.signal_ts)} · {fmtTime(d.signal_ts)}
          </span>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div className="featured-action" style={{ color: accent, textShadow: `0 0 24px ${accent}50` }}>
            {actionEff}
          </div>
          <div style={{ fontSize: 11, color: T.textDim, marginTop: 4, fontFamily: 'Roboto Mono, monospace' }}>
            {isTrade
              ? `${d.contracts ?? '—'}c · stop ${d.stop_pts ?? '—'}pt`
              : `Gate: ${d.gate_blocked || '—'}`}
          </div>
          {href && (
            <Link
              to={href}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                marginTop: 8,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: accent,
                textDecoration: 'none',
                borderBottom: `1px dashed ${accentBdr}`,
                paddingBottom: 1,
              }}
              title="Full decision detail (Ctrl/⌘-click for new tab)"
            >
              View full detail →
            </Link>
          )}
        </div>
      </div>

      {/* Stat grid */}
      <div className="featured-grid" style={{ borderBottomColor: accentBdr }}>
        {[
          { label: 'Grade',    node: <GradeBadge grade={d.grade} decision={d} /> },
          { label: 'ML Score', node: <span className="cell-value" style={{ color: mlColor(d.ml_score) }}>{Math.round(parseFloat(d.ml_score) || 0)}</span> },
          { label: 'Entry',    node: <span className="cell-value" style={{ color: T.text, fontSize: 14 }}>{fmtPrice(d.entry_price)}</span> },
          { label: 'Tier / Day', node: <span style={{ fontSize: 12, color: T.textDim }}>{d.tier_num || d.tier || d.day_of_week || '—'}</span> },
        ].map((c, i) => (
          <div key={i} className="featured-cell" style={{ borderRightColor: accentBdr }}>
            <div className="cell-label">{c.label}</div>
            {c.node}
          </div>
        ))}
      </div>

      {/* Stop / Target price row — only shown when at least one is present */}
      {isTrade && (d.stop_price || d.target_price || d.stop_pts) && (
        <div style={{
          padding: '10px 16px',
          display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
          borderBottom: `1px solid ${accentBdr}`,
        }}>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.textMuted, flexShrink: 0 }}>
            Levels
          </span>
          {(d.stop_price || d.stop_pts) && (
            <span style={{ fontFamily: 'Roboto Mono, monospace' }}>
              <span style={{ fontSize: 10, color: T.textMuted }}>SL </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.bear }}>
                {d.stop_price ? fmtPrice(d.stop_price) : `${d.stop_pts}pt`}
              </span>
            </span>
          )}
          {d.target_price && (
            <span style={{ fontFamily: 'Roboto Mono, monospace' }}>
              <span style={{ fontSize: 10, color: T.textMuted }}>TP </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.bull }}>
                {fmtPrice(d.target_price)}
              </span>
            </span>
          )}
        </div>
      )}

      {/* Claude AI verdict strip */}
      {d.claude_verdict && (
        <div style={{
          padding: '10px 16px',
          display: 'flex', alignItems: 'flex-start', gap: 10,
          borderBottom: `1px solid ${accentBdr}`,
        }}>
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
            textTransform: 'uppercase', color: 'var(--tv-text-muted)',
            marginTop: 2, flexShrink: 0,
          }}>Claude AI</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <ClaudeVerdictBadge verdict={d.claude_verdict} score={d.claude_score} reasoning={d.claude_reasoning} size="md" />
            <span className="mono" style={{ fontSize: 11, color: 'var(--tv-text-muted)' }}>
              score {d.claude_score ?? '—'}
            </span>
            {d.claude_reasoning && (
              <span style={{ fontSize: 11, color: 'var(--tv-text-dim)', width: '100%', marginTop: 2 }}>
                {d.claude_reasoning}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Exit / P/L strip — only when an exit is known for this decision */}
      {(() => {
        const p = computePnL(d);
        if (!p) return null;
        const color = p.pts > 0 ? T.bull : p.pts < 0 ? T.bear : T.textDim;
        const sign  = p.pts > 0 ? '+' : '';
        return (
          <div style={{
            padding: '12px 16px',
            display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 16,
            borderBottom: `1px solid ${accentBdr}`,
            background: p.pts > 0 ? T.bullBg : p.pts < 0 ? T.bearBg : 'transparent',
          }}>
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: T.textMuted,
            }}>
              {p.status === 'OPEN' ? 'Live P/L' : 'Exit · P/L'}
            </span>
            <span className="mono" style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>
              {sign}{p.pts}pt
            </span>
            {p.usd != null && (
              <span className="mono" style={{ fontSize: 14, fontWeight: 700, color }}>
                {p.usd > 0 ? '+' : ''}${p.usd}
              </span>
            )}
            {p.exitReason && (
              <span style={{ fontSize: 11, color: T.textMuted, fontStyle: 'italic' }}>
                {p.exitReason}
              </span>
            )}
          </div>
        );
      })()}

      {/* Meta row */}
      <div className="featured-meta" style={{ borderBottomColor: accentBdr }}>
        {(() => {
          // strategies_fired comes through as an array from most strategies
          // but as a VARCHAR string from FAVOLA/GEMINI/NCHE backends.
          // Normalize so .join() never crashes the render (caused the prod
          // blank-page outage on 2026-06-18 when FAVOLA/GEMINI rows landed).
          const raw = d.strategies_fired;
          const firedList = Array.isArray(raw)
            ? raw
            : (raw ? String(raw).split(',').map(s => s.trim()).filter(Boolean) : []);
          if (firedList.length === 0) return null;
          return (
            <span><span style={{ color: T.textMuted }}>Fired: </span>
              <span className="mono" style={{ color: T.text }}>{firedList.join(', ')}</span></span>
          );
        })()}
        {d.src && (
          <span><span style={{ color: T.textMuted }}>Src: </span>
            <span className="mono" style={{ color: T.text }}>{d.src}</span></span>
        )}
        {d.rsi != null && (
          <span><span style={{ color: T.textMuted }}>RSI: </span>
            <span className="mono" style={{ color: T.text }}>{parseFloat(d.rsi).toFixed(1)}</span></span>
        )}
        {d.macd_hist != null && (
          <span><span style={{ color: T.textMuted }}>MACD: </span>
            <span className="mono" style={{ color: T.text }}>{parseFloat(d.macd_hist).toFixed(2)}</span></span>
        )}
      </div>

      {/* Reason */}
      {(d.block_reason || d.reason || d.category_reason) && (
        <div className="featured-reason">
          {d.block_reason || d.reason}
          {d.category_reason && (
            <div style={{ marginTop: 4, fontSize: 10, color: T.textMuted, fontStyle: 'normal' }}>
              Category reason: {d.category_reason}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
